import { NgModule, inject } from '@angular/core';
import { RedirectFunction, Routes, Router, RouterModule } from '@angular/router';
import { AuthGuard } from 'src/app/features/services/users/Auth/auth.guard';
import { publicStatusHostGuard } from 'src/app/features/status-viewer/public-status-host.guard';

const statusLinkResolver = () =>
  import('./features/status-viewer/status-link-resolver').then((m) => m.StatusLinkResolverComponent);

/**
 * Classic UI internal status page URL → in-app viewer. A function rather than a string
 * redirect because an absolute string redirect drops the query string (`?theme=`).
 */
const toInternalStatusViewer: RedirectFunction = ({ params, queryParams, fragment }) =>
  inject(Router).createUrlTree(['/', 'status', 'internal', params['slug']], {
    queryParams,
    fragment: fragment ?? undefined,
  });

export const routes: Routes = [
  // ── Public status pages ───────────────────────────────────────────────────
  // These must stay above the AuthGuard-protected routes below: the status page
  // serves anonymous visitors, and anything reaching the application shell first
  // would bounce them to a login screen for an account they do not have.

  // An alias host or a customer's custom domain has no application to route to —
  // the hostname is the page identifier — so the viewer claims the whole origin.
  {
    path: '',
    canMatch: [publicStatusHostGuard],
    loadChildren: () =>
      import('./features/status-viewer/status-viewer.routes').then(
        (m) => m.statusViewerCustomDomainRoutes,
      ),
  },
  // Application host, behind an explicit prefix so it can never collide with an
  // account subdomain.
  {
    path: 'status',
    loadChildren: () =>
      import('./features/status-viewer/status-viewer.routes').then((m) => m.statusViewerAppHostRoutes),
  },

  // Live notification links. Each ends in a literal segment, so they cannot swallow
  // arbitrary application URLs the way a bare two-segment matcher would.
  // TRANSITIONAL: these belong behind server-side redirects.
  {
    path: 'i/:statusId/:incidentId',
    loadComponent: statusLinkResolver,
    data: { entityType: 'incident' },
  },
  {
    path: 'm/:statusId/:maintenanceId',
    loadComponent: statusLinkResolver,
    data: { entityType: 'maintenance' },
  },
  { path: ':org/:slug/verify', loadComponent: statusLinkResolver, data: { subscriptionAction: 'verify' } },
  { path: ':org/:slug/unsubscribe', loadComponent: statusLinkResolver, data: { subscriptionAction: 'unsubscribe' } },
  { path: ':org/:slug/manage', loadComponent: statusLinkResolver, data: { subscriptionAction: 'manage' } },
  { path: ':slug/verify', loadComponent: statusLinkResolver, data: { subscriptionAction: 'verify' } },
  { path: ':slug/unsubscribe', loadComponent: statusLinkResolver, data: { subscriptionAction: 'unsubscribe' } },
  { path: ':slug/manage', loadComponent: statusLinkResolver, data: { subscriptionAction: 'manage' } },

  // Classic UI internal status page URLs, still produced by the API and present in sent
  // emails. Each carries a literal segment, so no account route can match them.
  // TRANSITIONAL: drop once links are generated in the `/status/internal/:slug` form.
  { path: ':org/status-main-page/internal/:slug', pathMatch: 'full', redirectTo: toInternalStatusViewer },
  { path: ':org/internal/:slug', pathMatch: 'full', redirectTo: toInternalStatusViewer },

  {
    path: 'org',
    loadChildren: () =>
      import('./features/auth/auth.module').then((m) => m.AuthModule),
  },
  {
    path: 'error',
    loadChildren: () =>
      import('./features/errors/errors.routes').then((m) => m.ERRORS_ROUTES),
  },
  // Public email action links (ack/assign/close/escalate/voice) — must stay above AuthGuard layout.
  {
    path: ':subdomain/alert',
    loadChildren: () =>
      import('./features/email-actions/email-actions.module').then((m) => m.EmailActionsModule),
  },
  {
    path: ':subdomain/incident',
    loadChildren: () =>
      import('./features/email-actions/email-actions.module').then((m) => m.EmailActionsModule),
  },
  {
    path: ':subdomain/expired-installation',
    loadComponent: () =>
      import('./features/expired-installation/expired-installation.component').then((m) => m.ExpiredInstallationComponent),
  },
  {
    path: '',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/layout.module').then((m) => m.LayoutModule),
  },
  { path: '', redirectTo: 'org/account', pathMatch: 'full' },
  { 
    path: '**', 
    canActivate: [AuthGuard],  
    loadChildren: () =>  import('./pages/layout.module').then((m) => m.LayoutModule), 
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {})],
  exports: [RouterModule],
})
export class AppRoutingModule {}
